import { useMemo } from 'react';
import jwt_decode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLangManagement } from './useLangManagement';
import { redirectBaskets, RouteNames, EBHRMembers, RoleNames } from '../constans/routes';
import { updateUserLangAndId, updateUserTokens } from '../redux/slices/auth.slice';
import { RootState } from '../redux/store';
import {
  getUserInfo,
  ILoginRequest,
  loginRequest,
  microsoftLogin,
  switchOrganizationRequest,
  updateUserPreferencesRequest,
} from '../services/auth';

interface IDecodedToken {
  exp: number;
  roles: RoleNames[];
  organizationId: string | null;
  user_id: string;
}

export const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((store: RootState) => store);
  const { userData } = auth;
  const { allowedSurveys } = userData;
  const { changeLanguage } = useLangManagement();

  const userRolesFromToken = useMemo<{
    roles: RoleNames[];
    organizationId: string | null;
    user_id: string | null;
  }>(() => {
    if (userData?.access) {
      const decodedToken: IDecodedToken = jwt_decode(userData?.access);
      const { roles, organizationId, user_id } = decodedToken;
      return { roles, organizationId, user_id };
    }
    return { roles: [], organizationId: null, user_id: null };
  }, [userData.access]);

  const getRouteFromBaskets = (token: string) => {
    const decodedToken: IDecodedToken = jwt_decode(token);
    const { roles } = decodedToken;
    const findRoute = Object.keys(redirectBaskets).find((key: string) => {
      return roles.find((role: RoleNames) => {
        return redirectBaskets[key as keyof typeof redirectBaskets].roles.includes(role);
      });
    });
    const result = findRoute ? redirectBaskets[findRoute as keyof typeof redirectBaskets] : null;
    return result?.routeName || RouteNames.Surveys;
  };

  const refreshUserInfo = async () => {
    const userInfo = await getUserInfo();
    const { sub, locale, allowed_surveys = [] } = userInfo;
    dispatch(updateUserLangAndId({ lang: locale, userId: sub, allowedSurveys: allowed_surveys }));
    return;
  };

  const handleMicrosoftLogin = async (accessToken: string) => {
    const userAuth = await microsoftLogin(accessToken);
    if (userAuth) {
      const route = await getRouteFromBaskets(userAuth?.access);
      dispatch(updateUserTokens(userAuth));
      const userInfo = await getUserInfo();
      const { sub, locale, allowed_surveys = [] } = userInfo;
      await changeLanguage(locale);
      dispatch(updateUserLangAndId({ lang: locale, userId: sub, allowedSurveys: allowed_surveys }));
      return navigate(route);
    }
    return;
  };

  const login = async (credentials: ILoginRequest) => {
    const userAuth = await loginRequest(credentials);

    if (userAuth) {
      const route = await getRouteFromBaskets(userAuth?.access);
      dispatch(updateUserTokens(userAuth));
      const userInfo = await getUserInfo();
      const { sub, locale, allowed_surveys = [] } = userInfo;
      await changeLanguage(locale);
      dispatch(updateUserLangAndId({ lang: locale, userId: sub, allowedSurveys: allowed_surveys }));
      return navigate(route);
    }
    return;
  };

  const switchOrganization = async (organizationId: string) => {
    try {
      const tokens = await switchOrganizationRequest(organizationId, userData.refresh || '');

      if (!tokens?.access || !tokens?.refresh) {
        throw new Error('Invalid token response');
      }
      dispatch(updateUserTokens(tokens));

      const userInfo = await getUserInfo();
      const { sub, locale, allowed_surveys = [] } = userInfo;

      await changeLanguage(locale);
      dispatch(
        updateUserLangAndId({
          lang: locale,
          userId: sub,
          allowedSurveys: allowed_surveys,
        })
      );
      window.location.href = `/${RouteNames.Surveys}`;
    } catch (e) {
      console.error('Switch organization failed', e);
    }
  };

  const tokenAndRoleValidation = (requiredRoles: string[]) => {
    const { roles } = userRolesFromToken;
    if (roles) {
      return !!roles.find((role: string) => requiredRoles.includes(role));
    }
    return false;
  };

  const updateUserPreferences = async (lang: string) => {
    const { userId } = userData;
    return await updateUserPreferencesRequest(userId, lang);
  };

  const logOut = async () => {
    return navigate(RouteNames.Logout);
  };

  const isEBHRMember = !!EBHRMembers.find(role => userRolesFromToken?.roles?.includes(role));
  const isITAdministrator = !!userRolesFromToken?.roles?.includes(RoleNames.ITAdministrator);

  return {
    login,
    logOut,
    tokenAndRoleValidation,
    userRolesFromToken,
    userData,
    isEBHRMember,
    isITAdministrator,
    allowedSurveys,
    refreshUserInfo,
    getRouteFromBaskets,
    updateUserPreferences,
    handleMicrosoftLogin,
    switchOrganization,
  };
};
